import { doc, onSnapshot } from 'firebase/firestore';
import type { DocumentData, Firestore } from 'firebase/firestore';
import { STAKKO_PLAN_APPS } from '../config';
import type { StakkoAppId, StakkoEntitlements, StakkoPlan } from '../types';

export const FREE_ENTITLEMENTS: StakkoEntitlements = {
  plan: 'free',
  apps: {},
  creditsMonthly: 0,
  validUntil: null,
  legacyPremium: false,
};

/**
 * Přejmenované aplikace (rebrand 10/2026). Stará data v `vibiz_entitlements`
 * mají klíče podle původních jmen, takže je při čtení mapujeme na nová ID.
 */
const LEGACY_APP_IDS: Record<string, StakkoAppId> = {
  cortex: 'noddo',
  eventer: 'planno',
  gymapp: 'reppo',
  ledger: 'dukato',
  vitae: 'dailo',
};

function normalizeAppGrants(
  rawApps: Record<string, boolean> | undefined,
): Partial<Record<StakkoAppId, boolean>> {
  const out: Partial<Record<StakkoAppId, boolean>> = {};
  for (const [key, value] of Object.entries(rawApps ?? {})) {
    const id = (LEGACY_APP_IDS[key] ?? key) as StakkoAppId;
    if (value === true) out[id] = true;
    else if (out[id] === undefined) out[id] = value;
  }
  return out;
}

function isExpired(validUntil?: string | null): boolean {
  if (!validUntil) return false;
  // Make.com umí poslat jen datum (YYYY-MM-DD). Ber ho jako konec dne, ne
  // půlnoc na začátku – jinak by uživatel přišel o poslední den.
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(validUntil)
    ? `${validUntil}T23:59:59Z`
    : validUntil;
  const time = new Date(normalized).getTime();
  return Number.isFinite(time) && time < Date.now();
}

function deriveEntitlements(
  raw: DocumentData | undefined,
  legacyPremium: boolean,
): StakkoEntitlements {
  const explicitPlan = raw?.plan as StakkoPlan | undefined;
  const plan = explicitPlan ?? (legacyPremium ? 'plus' : 'free');
  const expired = isExpired(typeof raw?.validUntil === 'string' ? raw.validUntil : null);
  const effectivePlan: StakkoPlan = expired ? 'free' : plan;

  const apps: Partial<Record<StakkoAppId, boolean>> = {
    // Explicitní `plan: free` v entitlements má přednost před starým
    // users/{uid}.isPremiumRozvrh. Jinak by zrušené předplatné zůstalo aktivní.
    ...(legacyPremium && effectivePlan !== 'free' ? { vserozvrh: true } : {}),
    ...normalizeAppGrants(raw?.apps as Record<string, boolean> | undefined),
  };

  return {
    plan: effectivePlan,
    apps,
    creditsMonthly:
      typeof raw?.creditsMonthly === 'number'
        ? raw.creditsMonthly
        : effectivePlan === 'pro'
          ? 400
          : effectivePlan === 'plus'
            ? 100
            : 0,
    validUntil: typeof raw?.validUntil === 'string' ? raw.validUntil : null,
    source: typeof raw?.source === 'string' ? raw.source : undefined,
    legacyPremium,
  };
}

/**
 * Sleduje `vibiz_entitlements/{uid}` a zároveň staré `users/{uid}.isPremium`,
 * aby platby a ruční granty fungovaly i během migrace.
 */
export function subscribeStakkoEntitlements(
  db: Firestore,
  uid: string,
  onData: (entitlements: StakkoEntitlements) => void,
): () => void {
  let raw: DocumentData | undefined;
  let legacyPremium = false;

  const emit = () => onData(deriveEntitlements(raw, legacyPremium));

  const unsubEntitlements = onSnapshot(
    doc(db, 'vibiz_entitlements', uid),
    (snap) => {
      raw = snap.exists() ? snap.data() : undefined;
      emit();
    },
    (error) => {
      console.warn('[stakko] entitlements se nepodařilo načíst', error);
      raw = undefined;
      emit();
    },
  );

  const unsubLegacy = onSnapshot(
    doc(db, 'users', uid),
    (snap) => {
      const data = snap.data();
      legacyPremium = data?.isPremium === true || data?.isPremiumRozvrh === true;
      emit();
    },
    () => {
      legacyPremium = false;
      emit();
    },
  );

  return () => {
    unsubEntitlements();
    unsubLegacy();
  };
}

/**
 * Má uživatel placený přístup k dané aplikaci?
 * `appId` vynech jen tehdy, když tě zajímá „má vůbec nějaký placený plán“.
 */
export function hasStakkoPremium(
  entitlements: StakkoEntitlements,
  appId?: StakkoAppId,
): boolean {
  if (entitlements.plan === 'pro') return true;
  if (!appId) return entitlements.plan !== 'free' || Object.values(entitlements.apps).some(Boolean);
  if (entitlements.apps[appId] === true) return true;
  if (entitlements.plan === 'plus') return STAKKO_PLAN_APPS.plus.includes(appId);
  return false;
}
